import { publicAssetUrl } from "@/lib/public-image-url";
import type { CityGuide } from "./types";

const UPDATED = "2026-05-14";

export const CITY_GUIDES: CityGuide[] = [
  {
    slug: "london",
    city: "London",
    country: "United Kingdom",
    metaTitle: "Classic Car Hire in London: Guide to Driving and Hiring Classics",
    metaDescription:
      "Editorial guide to hiring a classic car in London: classic-car culture, the best drives within reach, ULEZ and congestion notes, paperwork, and live listings.",
    tldr:
      "London is BookAclassic's single largest market. The city has the densest concentration of classic-car owners in the UK, but the ULEZ, the Congestion Charge, and tight streets shape how a hire works in practice. Most London bookings are weddings, films, and short transfers; the best driving happens an hour outside the M25, in the Surrey Hills, the Chilterns, and the Kent weald.",
    hero: {
      image: publicAssetUrl("cars/1044699/legacy-1135863_1280w.webp"),
      alt: "1958 Bentley S1 available to hire in London",
    },
    sections: [
      {
        heading: "Classic-car culture in London",
        body:
          "London hosts the Royal Automobile Club, the RAC Concours, the London Concours, the Regent Street Motor Show (start of the London-to-Brighton Veteran Car Run, the world's longest-running motoring event since 1896), Salon Privé, and Bicester Heritage runs an hour up the M40. The capital's classic-car centre of gravity sits in West London (Chelsea, Kensington) and around Bicester for storage.",
      },
      {
        heading: "ULEZ, Congestion Charge, and historic-vehicle exemption",
        body:
          "London's Ultra Low Emission Zone (ULEZ) covers all London boroughs since 2023. Vehicles built before 1 January 1973 are classified as historic and exempt from ULEZ once registered with TfL. Cars built after that date may still be exempt if they pass Euro 3 (petrol) or Euro 6 (diesel), although most classics do not. The Congestion Charge (£15 daily within central London Mon–Fri 07:00–18:00, weekends 12:00–18:00) is separate; classic-car owners listing for London weddings should confirm whether their car is registered for historic-vehicle exemption.",
      },
      {
        heading: "Best drives within reach",
        body:
          "The Surrey Hills via Box Hill (40 minutes from central London), the Chilterns via Henley-on-Thames (1 hour), and the Kent weald via Sevenoaks (45 minutes) all give you twisty B-roads inside a day-trip radius. For a longer weekend, head to the Cotswolds (2 hours up the A40) or the New Forest (2 hours down the M3).",
      },
      {
        heading: "Suitable occasions",
        body:
          "Weddings (chauffeured), red-carpet film premieres, prom transport (Rolls-Royce Silver Shadow and Bentley S2 dominate), corporate event arrivals, and film and TV picture-car bookings. Self-drive is less common; most owners prefer to drop off and collect.",
      },
    ],
    drives: [
      { name: "Surrey Hills loop via Box Hill", description: "≈ 60 miles, 2–3 hours. Twisty B-roads with views over the North Downs." },
      { name: "Chilterns and Hambleden Valley", description: "≈ 75 miles. Quiet roads through Buckinghamshire and Oxfordshire villages." },
      { name: "Kent weald via Sevenoaks and Tonbridge", description: "≈ 90 miles. Mix of A-road and B-road; good lunch stops." },
      { name: "Goodwood weekend", description: "≈ 65 miles south. Combine the Revival, the Festival of Speed, or the Members' Meeting with a B-road return via the South Downs." },
    ],
    paperwork: [
      "UK driving licence holders: standard insurance applies via BookAclassic.",
      "International visitors: bring your home licence and an International Driving Permit if your home country is not on the UK's recognised-licence list.",
      "Cars built before 1 January 1973: ULEZ-exempt once registered as 'historic' with TfL.",
      "Congestion Charge: applies during charging hours regardless of vehicle age unless specifically exempted.",
    ],
    faqs: [
      {
        q: "Do I pay ULEZ on a classic car hire in London?",
        a: "Cars first registered before 1 January 1973 and registered as 'historic' with the DVLA / TfL are exempt. Most BookAclassic London listings make this explicit. Cars built after that date that don't meet Euro 3 (petrol) will incur the daily charge unless your listing confirms exemption.",
      },
      {
        q: "Can I get married in central London with a classic car?",
        a: "Yes. Most London wedding hires are chauffeured, the chauffeur handles parking and the Congestion Charge, and the car waits in a pre-arranged spot near the venue. Pre-1973 cars don't pay ULEZ.",
      },
      {
        q: "Is self-drive available in London?",
        a: "Some, but most owners prefer to deliver the car to the edge of London (e.g. Bicester, Chiswick, Surrey) and let the renter drive from there. Filter listings by 'self-drive' to see options.",
      },
    ],
    listingsQuery: { location: "London" },
    related: {
      cities: ["cotswolds", "paris"],
      useCases: ["wedding-car-hire", "film-and-tv-car-hire"],
      models: ["jaguar-e-type", "rolls-royce-silver-shadow", "aston-martin-db5"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "paris",
    city: "Paris",
    country: "France",
    metaTitle: "Classic Car Hire in Paris: Guide to Driving, Crit'Air, and Best Routes",
    metaDescription:
      "Editorial guide to hiring a classic car in Paris and Île-de-France: classic-car culture, Crit'Air ZFE notes for historic vehicles, recommended drives, and live listings.",
    tldr:
      "Paris is the largest classic-car market in mainland Europe. The city's Crit'Air low-emission zone (ZFE-m) restricts older vehicles inside the Boulevard Périphérique on weekdays. Cars over 30 years old can apply for a 'véhicule de collection' exemption. Best drives leave the city: the Vallée de Chevreuse, Fontainebleau, and the Loire Valley.",
    hero: {
      image: publicAssetUrl("cars/1058005/legacy-1227067_1280w.webp"),
      alt: "Classic Mercedes-Benz 450 SL available to hire in Paris",
    },
    sections: [
      {
        heading: "Classic-car culture in Paris",
        body:
          "Paris hosts Rétromobile in early February at Porte de Versailles, the largest classic-car show in Europe by visitor count, and Chantilly Arts & Élégance Richard Mille in September. The Traversée de Paris en Anciennes runs every January and July with hundreds of classic cars crossing the city. The market favours French makes (Citroën DS, Peugeot 504, Renault 4) and Italian roadsters.",
      },
      {
        heading: "Crit'Air, ZFE-m, and the 'collection' exemption",
        body:
          "The Crit'Air low-emission zone covers all of Paris and most of the inner suburbs (ZFE-m, since 2025 enforcement). Vehicles older than 30 years can register as 'véhicule de collection' with the Fédération Française des Véhicules d'Époque (FFVE) and a 'Carte Grise de Collection', which exempts them from Crit'Air restrictions. Listings should disclose whether the car is collection-registered.",
      },
      {
        heading: "Best drives within reach",
        body:
          "The Vallée de Chevreuse (45 minutes south-west) gives twisty rural roads through Île-de-France's hardwood forest. Fontainebleau (1 hour south-east) offers long straight forest roads and a château. The Loire Valley (2 hours south) is the classic French touring run, with Chenonceau, Chambord, and Villandry within a day's loop.",
      },
    ],
    drives: [
      { name: "Vallée de Chevreuse loop", description: "≈ 100 km, half-day. Forest, château villages, and the route through Dampierre and Chevreuse." },
      { name: "Fontainebleau and the Forêt", description: "≈ 130 km. Long forest straights and tight bends; Sunday classic-car meets at the château." },
      { name: "Loire Valley week", description: "Paris → Chenonceau → Chambord → Villandry → Tours. 600 km over three to five days." },
    ],
    paperwork: [
      "EU and EEA licence holders: no additional paperwork beyond passport and licence.",
      "UK, US, AU, NZ, CA: licence + International Driving Permit (1949 Convention) recognised.",
      "Crit'Air exemption requires the car to be registered as an FFVE collection vehicle. Check the listing.",
    ],
    faqs: [
      {
        q: "Can I drive a classic car in central Paris?",
        a: "Yes, if the car is registered as a 'véhicule de collection' (Carte Grise de Collection). Most listings on BookAclassic in the Paris market hold this status. Without it, ZFE-m daytime restrictions apply on weekdays.",
      },
      {
        q: "Best Parisian classic for a wedding?",
        a: "Citroën DS for elegance, Fiat 500 or 2CV for a lighter, more playful look. Pagoda SL or E-Type if you want a non-French grand tourer.",
      },
    ],
    listingsQuery: { location: "Paris" },
    related: {
      cities: ["london", "cote-dazur"],
      useCases: ["wedding-car-hire", "film-and-tv-car-hire"],
      models: ["volkswagen-beetle", "fiat-500-classic", "mercedes-pagoda"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "rome",
    city: "Rome",
    country: "Italy",
    metaTitle: "Classic Car Hire in Rome: Guide to ZTL, Wedding Cars, and Best Drives",
    metaDescription:
      "Editorial guide to hiring a classic car in Rome: Fiat 500 and Cinquecento wedding hires, ZTL navigation, and day-trip drives to Castelli Romani.",
    tldr:
      "Rome is the spiritual home of the Fiat 500 wedding hire. The city's ZTL (limited-traffic zone) covers most of the historic centre and is camera-enforced; classic-car wedding operators typically hold ZTL permits. Best drives leave the city for Castelli Romani to the south-east, Lake Bracciano to the north, or the Appian Way for short photo-driven loops.",
    hero: {
      image: publicAssetUrl("cars/1034107/legacy-1145543_1280w.webp"),
      alt: "Classic Alfa Romeo Spider available to hire in Rome",
    },
    sections: [
      {
        heading: "Classic-car culture in Rome",
        body:
          "The Fiat 500 (Nuova and later 500L) is the unofficial mascot of Roman classic-car hire. Vespa is the two-wheel equivalent. Roman classic-car culture is less concours-focused than Milan or Turin and more lifestyle-oriented. Most listings are photogenic city cars rather than serious touring machinery.",
      },
      {
        heading: "ZTL and central-city access",
        body:
          "Rome's ZTL covers the centro storico (Tridente, Pantheon, Trastevere), Trastevere, and the San Lorenzo / Testaccio fringe zones. The ZTL is camera-enforced and fines for unauthorised entry are around €70–€100 per camera. Wedding-hire operators usually carry a ZTL permit for their specific cars; confirm this in the listing before booking a centro storico ceremony.",
      },
      {
        heading: "Best drives within reach",
        body:
          "Castelli Romani (Frascati, Castel Gandolfo, Nemi) sits 30 minutes south-east on the Via Appia and gives short loops with food stops. Lake Bracciano (45 minutes north) has a single ring road around the volcanic lake. The Via Appia Antica itself, immediately south of the centre, is a perfect short photo route for vintage cars.",
      },
    ],
    drives: [
      { name: "Via Appia Antica + Castelli Romani", description: "≈ 80 km half-day loop with lunch in Frascati or Castel Gandolfo." },
      { name: "Lake Bracciano ring", description: "≈ 30 km around the lake; one of Lazio's quietest drives." },
      { name: "Tuscia and Bagnoregio", description: "≈ 250 km north-west day trip via Civita di Bagnoregio." },
    ],
    paperwork: [
      "EU / EEA licence: no extras.",
      "US, UK, AU, NZ, CA, ZA: licence + International Driving Permit recognised (1949 Convention).",
      "ZTL access: confirm the operator's permit covers the venue area.",
    ],
    faqs: [
      {
        q: "Do I need a ZTL permit to be married at the Pantheon?",
        a: "Yes. The centro storico ZTL covers the Pantheon. Most Rome wedding-car operators hold permits for their specific cars and will arrange the route. Confirm before booking.",
      },
      {
        q: "Which classic for a Roman wedding?",
        a: "Fiat 500 (Cinquecento) for the photogenic Italian look, Lancia Aurelia or Alfa Romeo Giulietta Spider for something more grown-up. Cabriolets are popular because Rome is warm seven months of the year.",
      },
    ],
    listingsQuery: { location: "Rome" },
    related: {
      cities: ["tuscany", "paris"],
      useCases: ["wedding-car-hire"],
      models: ["fiat-500-classic", "mini-classic"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "tuscany",
    city: "Tuscany",
    country: "Italy",
    metaTitle: "Classic Car Hire in Tuscany: Routes, Vineyards, and Wedding Hire",
    metaDescription:
      "Editorial guide to hiring a classic car in Tuscany: best driving roads (Chianti, Val d'Orcia), wedding hire in Florence and Siena, and live listings.",
    tldr:
      "Tuscany is one of the most-requested driving destinations in Europe. The Chianti hills, Val d'Orcia, and Strada Provinciale 146 between Pienza and Montepulciano are postcard roads built for classic cars. Florence and Siena drive the wedding-hire market.",
    hero: {
      image: publicAssetUrl("cars/1045843/legacy-1158523_1280w.webp"),
      alt: "Vintage Fiat 500 available to hire in Tuscany",
    },
    sections: [
      {
        heading: "Classic-car culture in Tuscany",
        body:
          "Tuscany is the route of the Mille Miglia Storica, which crosses the region every spring on its way from Brescia to Rome and back. Florence has a small but committed classic-car community; the region's countryside is the real attraction.",
      },
      {
        heading: "Best driving routes",
        body:
          "SP146 (Pienza–Montepulciano) is the Val d'Orcia's signature road, with cypress-lined ridges and views of San Quirico. The SR222 (Chiantigiana) runs Florence–Siena via Greve and Castellina, the spine of Chianti. The Passo della Futa (SS65) crosses the Apennines on the old Mille Miglia route, with fast, sweeping bends.",
      },
      {
        heading: "Wedding hire",
        body:
          "Florence (Piazzale Michelangelo, Boboli) and Siena (the duomo, Crete Senesi) drive most wedding bookings. Most operators offer cabriolet SL Pagodas, Alfa Spiders, Fiat 500s, or Lancia Aurelias, often chauffeured.",
      },
    ],
    drives: [
      { name: "Val d'Orcia loop (SP146)", description: "≈ 80 km. Pienza → Montepulciano → Montalcino. The most-photographed road in Tuscany." },
      { name: "Chiantigiana (SR222)", description: "≈ 70 km. Florence → Greve → Castellina → Siena via the Chianti spine." },
      { name: "Passo della Futa (Mille Miglia route)", description: "≈ 60 km of Apennine sweeping bends, north out of Florence." },
    ],
    paperwork: [
      "EU / EEA licence: standard.",
      "Non-EU visitors: licence + International Driving Permit.",
      "ZTLs apply in Florence and Siena city centres. Operators carry permits.",
    ],
    faqs: [
      {
        q: "Best base for a Tuscan classic-car tour?",
        a: "Florence for first-day pickups, Siena or Montepulciano for the second leg. Most operators will deliver the car to your hotel or villa.",
      },
      {
        q: "Can I drive a classic into a vineyard estate?",
        a: "Most vineyards welcome classic cars (some specifically want them for events). Check the access road condition with the owner because some Tuscan strade bianche are rough gravel.",
      },
    ],
    listingsQuery: { location: "Tuscany" },
    related: {
      cities: ["rome", "cote-dazur"],
      useCases: ["wedding-car-hire", "driving-experience-hire"],
      models: ["fiat-500-classic", "mercedes-pagoda", "porsche-911-air-cooled"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "cote-dazur",
    city: "Côte d'Azur",
    country: "France",
    metaTitle: "Classic Car Hire on the Côte d'Azur: Monaco, Nice, and Best Drives",
    metaDescription:
      "Editorial guide to hiring a classic car on the French Riviera: corniche routes, Monaco GP heritage, wedding venues, and live listings around Nice, Cannes, and Monaco.",
    tldr:
      "The Côte d'Azur is the densest concentration of high-end classic-car infrastructure in Europe, anchored by the Monaco Grand Prix and the Monaco Historique. The three corniches between Nice and Monaco are the region's signature drive. Best season is May–October; July–August is busy.",
    hero: {
      image: publicAssetUrl("cars/1029716/legacy-1027625_1280w.webp"),
      alt: "Classic Ford Mustang convertible available to hire on the Côte d'Azur",
    },
    sections: [
      {
        heading: "Classic-car culture on the Riviera",
        body:
          "Monaco runs both the modern Grand Prix and the Historique (alternating years, May). Cannes hosts the Concours d'Élégance during the Film Festival. Nice's Promenade des Anglais sees a steady weekend trickle of vintage cars year-round. Most listings cluster between Nice, Beaulieu, Villefranche, and Monaco.",
      },
      {
        heading: "The three corniches",
        body:
          "Grande Corniche (D2564) is the highest, fastest, and least busy route; Hitchcock used it in To Catch a Thief. Moyenne Corniche (N7) sits at middle altitude and joins Èze. Basse Corniche (D6098) is coastal, slow, and photogenic. The classic loop goes up the Grande, down the Moyenne to Èze, stops for lunch, and returns along the Basse.",
      },
      {
        heading: "Wedding and event hire",
        body:
          "Cabriolet 1960s SLs, E-Types, and Spider 2000s dominate Riviera weddings. Many operators include yacht or villa transfers as part of the package.",
      },
    ],
    drives: [
      { name: "Three Corniches loop (Nice ↔ Monaco)", description: "≈ 60 km. The defining Riviera drive." },
      { name: "Col de Turini", description: "≈ 90 km north of Nice. A Monte Carlo Rally pass with hairpins and forest sections." },
      { name: "Saint-Tropez via the Esterel coast road", description: "≈ 120 km west. Red-rock coast, slow traffic in summer." },
    ],
    paperwork: [
      "EU licence: standard.",
      "Non-EU: licence + IDP (1949 Convention).",
      "Monaco entry: standard EU border, but enforcement of parking and speed is strict.",
    ],
    faqs: [
      {
        q: "Best time of year for a Riviera classic-car trip?",
        a: "May (in Monaco Historique years) and September–October are the sweet spots: the weather is warm and dry, and the roads are less crowded than in July and August.",
      },
      {
        q: "Can I drive into Monaco?",
        a: "Yes, with caveats. Public parking in Monaco is camera-monitored and expensive; most classic-car operators recommend parking in Cap d'Ail or Beausoleil and walking in, or having a chauffeur drop and re-collect.",
      },
    ],
    listingsQuery: { location: "Nice" },
    related: {
      cities: ["tuscany", "paris"],
      useCases: ["wedding-car-hire", "driving-experience-hire"],
      models: ["porsche-911-air-cooled", "mercedes-pagoda", "jaguar-e-type"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "cotswolds",
    city: "Cotswolds",
    country: "United Kingdom",
    metaTitle: "Classic Car Hire in the Cotswolds: Driving Routes and Wedding Venues",
    metaDescription:
      "Editorial guide to hiring a classic car in the Cotswolds: best B-roads, market-town wedding venues, and live listings within easy reach of London.",
    tldr:
      "The Cotswolds are the easiest 'classic-car weekend' from London. Two hours up the M40 takes you to honey-stone villages with quiet B-roads. Bibury, Burford, Bourton-on-the-Water, and Chipping Campden anchor the wedding market; the Fosse Way and the Cotswold scarp drive the touring market.",
    hero: {
      image: publicAssetUrl("cars/1033124/legacy-1048801_1280w.webp"),
      alt: "1954 Sunbeam available to hire near the Cotswolds",
    },
    sections: [
      {
        heading: "Classic-car culture in the Cotswolds",
        body:
          "Bicester Heritage, on the eastern edge of the Cotswolds, is the UK's largest classic-car business cluster and runs the regular Bicester Heritage Scramble (six per year). The Cotswolds themselves are quieter; most classic-car activity is private weddings and self-drive touring.",
      },
      {
        heading: "Best drives",
        body:
          "The Fosse Way (A429) cuts straight through the region as a Roman road. The B4632 along the Cotswold scarp from Cheltenham to Broadway gives the best views. The B4068 between Stow and Cirencester is a quiet, classic B-road loop.",
      },
    ],
    drives: [
      { name: "Cotswold scarp (B4632)", description: "≈ 40 miles, Cheltenham → Winchcombe → Broadway. Views over the Severn Vale." },
      { name: "Fosse Way (A429) + B4068", description: "≈ 60 mile loop. Stow-on-the-Wold base." },
      { name: "Burford → Bibury → Cirencester", description: "≈ 35 miles through the postcard villages." },
    ],
    paperwork: [
      "UK licence holders: standard.",
      "No ULEZ or congestion zone in the Cotswolds proper.",
    ],
    faqs: [
      {
        q: "Best base for a Cotswolds classic-car weekend?",
        a: "Stow-on-the-Wold or Chipping Norton for central access; Cheltenham for hotel options; Broadway for the most-photographed village.",
      },
      {
        q: "Can the car be delivered from London?",
        a: "Yes. Most London operators will deliver to a Cotswolds hotel for a fee or include it in the day rate for longer hires.",
      },
    ],
    listingsQuery: { location: "Cotswolds" },
    related: {
      cities: ["london"],
      useCases: ["wedding-car-hire", "driving-experience-hire"],
      models: ["jaguar-e-type", "mgb", "porsche-911-air-cooled"],
    },
    updatedAt: UPDATED,
  },
];

export function getCityGuide(slug: string): CityGuide | undefined {
  return CITY_GUIDES.find((c) => c.slug === slug);
}
